/**
 * UPT-2079 L5 (web §5.4, §5.8) — messages du Générateur dont le `{detail}` est un code technique rendu en `<code dir="ltr">` (chemin
 * fautif d'une erreur du serveur, nom de champ, langue ou mot-clé d'une remarque) : jamais traduit, jamais interprété, toujours de
 * gauche à droite, même dans une page arabe. Le découpage autour des repères est celui de `PreviewMessage` (aucun HTML interprété).
 * UPT-2079 L5d (web §11.5) : les quatre codes `generator.repo.*` — `unavailable` se lit par sa raison (même libellé que l'état du
 * bouton, `{module}` en texte), `refused` rend son jeton de raison en `<code dir="ltr">` (jamais un autre message du serveur),
 * `failed` se lit par sa raison (inconnue : `remote`), `branch_exists` n'affiche rien du serveur.
 */
import { useIntl } from '@/react-intl';
import type { GeneratorIssue } from '../data/contracts';
import { PreviewMessage } from '../preview/PreviewMessage';
import type { DesignUiError } from '../specs/designErrors';
import { designErrorMessage } from '../specs/specI18n';
import {
  GENERATOR_ERROR_DETAIL_CODES,
  generatorErrorId,
  generatorIssueId,
  generatorRepoFailedId,
  generatorRepoUnavailableId,
  isGeneratorErrorCode,
} from './generatorI18n';
import { repoReasonToken } from './generatorRepoModel';

/**
 * Erreur d'une action du Générateur : codes `generator.*` (chemin fautif en `<code>`), sinon le libellé des dossiers et des specs.
 * `moduleCode` (code du module de l'exécution) complète `generator.repo.unavailable`, dont le libellé nomme le dépôt `Geode-module-<code>`.
 */
export function GeneratorErrorText({ error, moduleCode }: { error: DesignUiError; moduleCode?: string }) {
  const intl = useIntl();
  if (isGeneratorErrorCode(error.code)) {
    // Le jeton de raison choisit un libellé (jamais affiché) : les textes sont calculés HORS du JSX (règle 4a).
    if (error.code === 'generator.repo.unavailable') {
      if (error.message === 'no-credential' && error.credentialName) {
        const credentialText = intl.formatMessage({ id: 'moduleStudio.repoDepot.credential' }, { configuration: error.credentialName });
        return <>{credentialText}</>;
      }
      const unavailableId = generatorRepoUnavailableId(error.message);
      const unavailableText = intl.formatMessage({ id: unavailableId }, { module: moduleCode && moduleCode.length > 0 ? moduleCode : '?' });
      return <>{unavailableText}</>;
    }
    if (error.code === 'generator.repo.failed') {
      const failedId = generatorRepoFailedId(error.message);
      const failedText = intl.formatMessage({ id: failedId }, { configuration: error.credentialName ?? 'AZURE_DEVOPS_PAT' });
      return <>{failedText}</>;
    }
    const withDetail = (GENERATOR_ERROR_DETAIL_CODES as readonly string[]).includes(error.code);
    const detail = error.code === 'generator.repo.refused' ? repoReasonToken(error.message) : error.message && error.message.length > 0 ? error.message : '?';
    return <PreviewMessage id={generatorErrorId(error.code)} codes={withDetail ? { detail } : undefined} />;
  }
  return <>{designErrorMessage((id: string) => intl.formatMessage({ id }), error)}</>;
}

/**
 * Message d'une remarque du plan ; un code inconnu de ce web est rendu tel quel (jamais une clé composée). `workflow.invalid` se lit par son `detail`
 * (champ d'état, états, état initial, transition) en mots du Maker (UPT-2079 L5e).
 */
export function GeneratorIssueText({ issue }: { issue: Pick<GeneratorIssue, 'code' | 'detail'> }) {
  const id = generatorIssueId(issue.code, issue.detail);
  if (id === null) {
    return (
      <code dir="ltr" className="break-all">
        {issue.code}
      </code>
    );
  }
  return <PreviewMessage id={id} codes={{ detail: issue.detail }} />;
}
